import { useEffect, useState } from 'react';
import { RiErrorWarningFill } from 'react-icons/ri';
import { FaGear, FaPaperPlane, FaQuoteLeft } from 'react-icons/fa6';
import { FaExclamationCircle } from 'react-icons/fa';
import DashboardCard from 'common/components/dashboard/dashboard-card';
import DashboardMessage from 'components/shared/dashboard-message';
import DashboardWrapperLayout from 'layouts/dashboard-wrapper';
import {
  useGetAllClaimsQuery,
  useGetAllPlansQuery,
} from 'redux/api/ciosUsersApi/ciosUsersApi';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import ContactCard from 'components/shared/contact-card';
import { planStatusEnum } from 'common/utils/enums';
import { formatDateToHumanDate } from 'common/utils/formDateToHumanTime';
import TablePaginateWrapper from 'common/layouts/table-paginate-wrapper';
import moment from 'moment';
import Button from 'common/components/button';
import { ButtonGroup, Flex, Grid, Text } from '@chakra-ui/react';
import { BiSolidBadgeCheck } from 'react-icons/bi';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/features/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import PlanCreateButton from 'components/plans/plan-create-button';
import ContactSupportModal from 'components/shared/help-button';

const DashboardHome = () => {
  const { trackPageLoad, setFormData } = useUserTracker();
  const navigate = useNavigate();

  useEffect(() => {
    trackPageLoad('dashboardHome');
    setFormData({});
  }, []);

  const { data: claimsData, isLoading: isGetAllClaimsLoading } =
    useGetAllClaimsQuery({ refetchOnMountOrArgChange: true });
  const { data: plansData, isLoading: getAllPlansLoading } =
    useGetAllPlansQuery({ refetchOnMountOrArgChange: true });

  const userData = useSelector(selectUser);

  // Helper function to get pluralized 'day' text
  const getDaysRemainingText = (expirationDate) => {
    if (!expirationDate) return '0 days';
    const days = moment.utc(expirationDate).diff(moment.utc(), 'days');
    return `${days > 0 ? days : 0} ${days === 1 ? 'day' : 'days'}`;
  };

  // Create dashboard messages for plans needing activation
  const requiresActivationPlans = plansData?.filter(
    (plan) => plan.status === planStatusEnum.ACTIVATION_REQUIRED
  );

  const activationMessages =
    requiresActivationPlans?.map((plan) => {
      return {
        message: (
          <span>
            Your coverage plan at{' '}
            {plan?.property?.name || plan?.lease?.address?.streetAddress1} is
            awaiting activation.
          </span>
        ),
        title: (
          <span className='inline-flex items-center gap-2 justify-between w-full'>
            Activate Plan
            <span className='hidden sm:inline-flex uppercase font-mono text-sm px-2 py-0.5 border border-gray-600 text-gray-400 rounded-md'>
              {plan.id}
            </span>
          </span>
        ),
        path: `/dashboard/plans/activate/${plan.id}`,
        cta: 'Activate Plan',
      };
    }) || [];

  // Create dashboard messages for plans expiring soon
  const expiringPlans = plansData?.filter(
    (plan) => {
      const isRelevantStatus = [planStatusEnum.PENDING, planStatusEnum.QUOTE].includes(plan.status);
      if (!isRelevantStatus || !plan.expirationDate) return false;

      // A plan is considered "expiring" if its expiration date is today or in the future.
      const isExpired = moment.utc(plan.expirationDate).isBefore(moment.utc(), 'day');
      
      return !isExpired;
    }
  );

  const expirationMessages =
    expiringPlans?.map((plan) => {
      const isPending = plan.status === planStatusEnum.PENDING;
      const planType = isPending ? 'purchase' : 'quote';
      const titlePrefix = isPending ? 'Purchase Expires Soon' : 'Quote Expires Soon';
      const ctaText = isPending ? 'Complete Purchase' : 'Purchase Coverage';
      const daysText = getDaysRemainingText(plan.expirationDate);
      const formattedExpirationDate = formatDateToHumanDate(
        plan.expirationDate
      );
      const isExpiringToday = moment.utc(plan.expirationDate).isSame(moment.utc(), 'day');

      return {
        message: (
          <span>
            Your {planType} for coverage at{' '}
            {plan?.property?.name || plan?.lease?.address?.streetAddress1}{' '}
            {isExpiringToday ? (
              <>
                expires <span className='font-semibold'>today</span>.
              </>
            ) : (
              <>
                expires in <span className='font-semibold'>{daysText}</span> on{' '}
                <span className='font-semibold'>{formattedExpirationDate}</span>.
              </>
            )}
          </span>
        ),
        title: (
          <span className='inline-flex items-center gap-2 justify-between w-full'>
            {titlePrefix}
            <span className='hidden sm:inline-flex uppercase font-mono text-sm px-2 py-0.5 border border-gray-600 text-gray-400 rounded-md'>
              {plan.id}
            </span>
          </span>
        ),
        path: `/dashboard/plans/${planType}/${plan.id}`,
        cta: ctaText,
      };
    }) || [];

  const [currentPage, setCurrentPage] = useState(1);
  const messagesElements = [
    ...activationMessages.map((message, index) => (
      <DashboardMessage
        key={message.path}
        Icon={RiErrorWarningFill}
        title={message.title}
        description={message.message}
        path={message.path}
        buttonTitle={message.cta}
      />
    )),
    ...expirationMessages.map((message, index) => (
      <DashboardMessage
        key={message.path}
        Icon={RiErrorWarningFill}
        title={message.title}
        description={message.message}
        path={message.path}
        buttonTitle={message.cta}
      />
    )),
  ].filter(Boolean);
  const pageSize = 1;
  const totalCount = messagesElements.length;
  const paginatedMessages = messagesElements.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const buttons = (
    <ButtonGroup>
      <ContactSupportModal
        buttonProps={{
          title: 'Get Help from Support',
          theme: 'secondary',
        }}
        modalHeader='Contact Support'
      />
    </ButtonGroup>
  );

  return (
    <DashboardWrapperLayout
      title={userData ? `Welcome back, ${userData.firstName}` : `Welcome`}
      buttons={buttons}
    >
      <Grid className='grid-cols-1 md:grid-cols-[2fr_1fr] xl:grid-cols-[3fr_1fr] gap-8 grid-flow-dense relative'>
        {totalCount > 0 && (
          <div className='w-full flex flex-col gap-4 px-6 pb-6 pt-5 rounded-lg border border-gray-600 bg-black text-white'>
            <div className='flex-col flex gap-1'>
              <h2 className='text-lg font-medium'>
                Finish Setting Up Your Plan
              </h2>
              <Text className='text-base text-gray-500'>
                Complete these steps to finish setting up your plan.
              </Text>
            </div>
            <TablePaginateWrapper
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalCount={totalCount}
              pageSize={pageSize}
              showDivider={false}
              dark
            >
              {paginatedMessages}
            </TablePaginateWrapper>
          </div>
        )}

        <div className='w-full flex flex-col gap-4 px-6 pb-6 pt-5 rounded-lg border border-gray-200 row-span-2 md:col-start-2 md:sticky top-40 self-start'>
          <h2 className='text-lg font-medium'>Quick Start</h2>
          <div className='w-full grid grid-cols-1 gap-4'>
            <Flex className='p-5 flex-col gap-4 bg-gray-50 rounded-md border border-gray-200'>
              <Flex className='flex-col gap-1'>
                <h3 className='text-base font-medium'>New Quote</h3>
                <Text className='text-sm text-gray-500 max-w-sm'>
                  Get a free quote on ReLease Coverage for your next lease.
                </Text>
              </Flex>
              <ButtonGroup className='md:mt-2'>
                <PlanCreateButton
                  mode='quote'
                  title='Start Quote'
                  theme='secondary'
                  size='sm'
                />    
              </ButtonGroup>
            </Flex>
            <Flex className='p-5 flex-col gap-4 bg-gray-50 rounded-md border border-gray-200'>
              <Flex className='flex-col gap-1'>
                <h3 className='text-base font-medium'>Purchase Coverage</h3>
                <Text className='text-sm text-gray-500 max-w-sm'>
                  Purchase coverage for an upcoming lease you've already signed.
                </Text>
              </Flex>
              <ButtonGroup className='md:mt-2'>
                <PlanCreateButton
                  mode='purchase'
                  title='Purchase Now'
                  theme='secondary'
                  size='sm'
                />
              </ButtonGroup>
            </Flex>
          </div>
        </div>

        <div className='w-full flex flex-col gap-4 '>
          <h2 className='text-lg font-medium'>Quick Navigation</h2>
          <div className='w-full grid grid-cols-3 gap-4 mobile:grid-cols-2'>
            <DashboardCard
              loading={getAllPlansLoading}
              title='Plans'
              // count={plansData?.length ?? 0}
              path='/dashboard/plans'
              Icon={FaPaperPlane}
            />
            <DashboardCard
              loading={isGetAllClaimsLoading}
              title='Claims'
              // count={claimsData?.length ?? 0}
              path='/dashboard/claims'
              Icon={FaExclamationCircle}
            />
            <DashboardCard
              title='Settings'
              // count={0}
              path='/dashboard/settings'
              Icon={FaGear}
            />
          </div>
        </div>
        <ContactCard title='Contact Us'>
          Questions about your plan or with claims? Contact us by email, phone,
          or text and we can help you answer any questions you may have about
          purchasing a plan, making claims, and more.
        </ContactCard>
      </Grid>
    </DashboardWrapperLayout>
  );
};

export default DashboardHome;
